/**
 * Per-user app access (training/11-mobile-app-plan.md §5).
 *
 * The account owner decides, per user, which parts of Scada.Ink Cloud they get
 * — Remote View, Devices, Alarms — on which appliances, and which screens they
 * may open on each. This is the only authority for remote and alarm access:
 * an appliance's local users, roles, groups and screen permissions do not
 * apply to cloud users.
 *
 * Effective access = the user's access ∩ the account's packages ∩ the
 * appliances each package covers:
 *   views   — Remote Viewing, on appliances with remote viewing turned on
 *   alarms  — Alerts (SMS & Voice), every appliance on the account
 *   devices — either package, on the appliances that package covers
 *
 * Routes (owner only):
 *   GET  /api/portal/app-users                   → AppUser[] (owner, members, viewers, open invites)
 *   GET  /api/portal/app-users/catalog           → AppAccessCatalog
 *   PUT  /api/portal/app-users/:id/access        UserAppAccess → AppUser   (user id or invite id; not the owner)
 *   POST /api/portal/app-users/invites           InviteAppUserRequest → CreateRemoteViewerResponse (201)
 *
 * Browser-safe: no Node types.
 */

export type ApplianceScope = 'all' | string[];

export interface ScreenSelection {
  dashboards: number[];
  customPages: number[];
  reports: number[];
}

export interface UserAppAccess {
  views: {
    enabled: boolean;
    appliances: ApplianceScope;
    /** Per appliance id. An appliance not listed here means every screen on it. */
    screens: Record<string, 'all' | ScreenSelection>;
  };
  devices: { enabled: boolean; appliances: ApplianceScope };
  alarms: { enabled: boolean; appliances: ApplianceScope };
}

export type AppSection = 'views' | 'devices' | 'alarms';
export const APP_SECTIONS: readonly AppSection[] = ['views', 'devices', 'alarms'];

/** Everything, following new appliances and screens: the owner's, and the default for everyone else. */
export function fullAppAccess(): UserAppAccess {
  return {
    views: { enabled: true, appliances: 'all', screens: {} },
    devices: { enabled: true, appliances: 'all' },
    alarms: { enabled: true, appliances: 'all' },
  };
}

/** What a user can actually use now, after packages and coverage. */
export interface EffectiveAppAccess {
  views: string[];
  devices: string[];
  alarms: string[];
}

export type AppUserKind = 'user' | 'invite';

export interface AppUser {
  /** User id, or invite id for an invite not yet accepted. */
  id: string;
  kind: AppUserKind;
  email: string;
  name: string;
  role: 'owner' | 'member' | 'viewer';
  /** invited / expired apply to invites only. */
  status: 'active' | 'invited' | 'expired';
  access: UserAppAccess;
  /** false for the owner, whose access is always everything. */
  editable: boolean;
  lastLoginAt: string | null;
}

export interface AppCatalogAppliance {
  id: string;
  name: string;
  site: string;
  /** Which sections the account's packages cover on this appliance. */
  covered: Record<AppSection, boolean>;
  screens: {
    dashboards: Array<{ id: number; name: string }>;
    customPages: Array<{ id: number; name: string }>;
    reports: Array<{ id: number; name: string }>;
  };
  /** When the screen list last arrived from the appliance; null if never (it is not on the feed). */
  screensAsOf: string | null;
}

export interface AppAccessCatalog {
  packages: { views: boolean; alarms: boolean };
  appliances: AppCatalogAppliance[];
  /** Remote View's named-viewer allowance (null = unlimited) and what is used. */
  viewerSeats: { limit: number | null; used: number };
}

export interface InviteAppUserRequest {
  email: string;
  name: string;
  /** Omitted: full access. */
  access?: UserAppAccess;
}
